import { useContext, useState, useEffect, useMemo } from 'react';
import { RouterContext, goTo } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler, List, ListItem } from 't-lib-ui/structure';
import { AppState } from '../../context/general';
import { getAdminMagicSecret } from '../../connector/auth';
import { TextButton } from 't-lib-ui/widgets';
import { chevronBackIcon, openIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import AdminHeader from '../../components/admin/AdminHeader';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { useGetStuff, getCatalog } from '../../connector/api';
import { DiscoverConfig, RestCatalog } from '../../schema-copy/api-schema';
import { QueryStatus } from 't-lib-ui/connector';

interface InactiveFeed {
  feedId: string;
  /** Earliest day (YYYY-MM-DD) this feed was marked inactive across its usages. */
  inactiveSince: string;
  /** Where this feed is referenced in the discover config (e.g. "Collection: Tech"). */
  usages: string[];
}

interface FeedInfo {
  title: string;
  domain: string;
  siteUrl: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const daysInactive = (inactiveSince: string): number => {
  const since = Date.parse(inactiveSince);
  if (isNaN(since)) return 0;
  return Math.max(0, Math.floor((Date.now() - since) / DAY_MS));
};

const AdminInactiveFeedsPage: React.FC = () => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [feedInfoMap, setFeedInfoMap] = useState<Map<string, FeedInfo>>(new Map());

  const magic = getAdminMagicSecret() || adminSecret || '';

  useFlowFormGlobal();

  useEffect(() => {
    registerKeyHandler('adminInactiveFeedsPage', {
      'Escape': () => {
        goTo(routerCtx, '/a/admin/feeds');
        return true;
      },
    }, false, 0);
    return () => unregisterKeyHandler('adminInactiveFeedsPage');
  }, [routerCtx]);

  const { data: config, status: loadStatus } = useGetStuff<DiscoverConfig>('discover/config', magic);

  // Build the list of inactive feeds with where-used info, oldest inactivity first.
  const inactiveFeeds: InactiveFeed[] = useMemo(() => {
    if (!config) return [];
    const map = new Map<string, InactiveFeed>();
    const add = (feedId: string, since: string | undefined, usage: string) => {
      if (!since) return;
      const existing = map.get(feedId);
      if (existing) {
        existing.usages.push(usage);
        if (since < existing.inactiveSince) existing.inactiveSince = since;
      } else {
        map.set(feedId, { feedId, inactiveSince: since, usages: [usage] });
      }
    };

    for (const collection of config.collections ?? []) {
      const name = collection.title || collection.id;
      for (const feed of collection.feedIds)
        add(feed.feedId, feed.inactiveSince, `Collection: ${name}`);
    }

    return Array.from(map.values()).sort((a, b) => a.inactiveSince.localeCompare(b.inactiveSince));
  }, [config]);

  // Resolve titles / site URLs for the listed feeds.
  useEffect(() => {
    const auth = appState.state.authData;
    if (!auth) return;
    for (const { feedId } of inactiveFeeds) {
      if (feedInfoMap.has(feedId)) continue;
      getCatalog(auth, feedId).then((catalog: RestCatalog) => {
        setFeedInfoMap(prev => new Map(prev).set(feedId, {
          title: catalog.title,
          domain: catalog.domain,
          siteUrl: catalog.siteUrl || ''
        }));
      }).catch(() => {});
    }
  }, [inactiveFeeds, appState.state.authData]);

  const loading = loadStatus == QueryStatus.Loading || loadStatus == QueryStatus.Pending;

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
        <MainLayout
          selectedFeed="admin-inactive-feeds"
          right={({ onExpand }) => (
            <FlowForm main={true} className='full-margin'>
              <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
                <AdminHeader title="Inactive Feeds" onExpand={onExpand} />
                <p style={{ marginBottom: '24px', color: 'var(--gray-400)' }}>
                  {loading
                    ? 'Loading...'
                    : `${inactiveFeeds.length} discover feed${inactiveFeeds.length == 1 ? '' : 's'} with no post in the last 30 days. The daily activity job excludes these from the Discover and onboarding pages until they post again. Open a feed to check whether its site is still alive.`}
                </p>

                <ButtonBar size="small">
                  <TextButton
                    label="Back"
                    icon={chevronBackIcon}
                    onClick={() => goTo(routerCtx, '/a/admin/feeds')}
                    focussable="flow"
                  />
                </ButtonBar>

                <List>
                  {!loading && inactiveFeeds.length == 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                      No inactive feeds. Everything referenced on Discover has posted in the last 30 days.
                    </div>
                  )}
                  {inactiveFeeds.map(({ feedId, inactiveSince, usages }) => {
                    const info = feedInfoMap.get(feedId);
                    const days = daysInactive(inactiveSince);
                    return (
                      <ListItem key={feedId} focussable="normal" href={`/a/admin/feed/${feedId}`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500 }}>{info?.title || feedId}</div>
                            <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', marginTop: '4px' }}>
                              {info?.domain ? `${info.domain} • ` : ''}{feedId}
                            </div>
                            <div style={{ fontSize: '0.85em', color: '#b8860b', marginTop: '4px' }}>
                              Inactive for {days} day{days == 1 ? '' : 's'} (since {inactiveSince})
                            </div>
                            <div style={{ fontSize: '0.8em', color: 'var(--gray-500)', marginTop: '4px' }}>
                              Used in: {usages.join(', ')}
                            </div>
                          </div>
                          {info?.siteUrl && (
                            <TextButton
                              label="Visit site"
                              icon={openIcon}
                              onClick={() => window.open(info.siteUrl, '_blank', 'noopener,noreferrer')}
                              focussable="flow"
                              size="small"
                              type="minor"
                            />
                          )}
                        </div>
                      </ListItem>
                    );
                  })}
                </List>
              </div>
            </FlowForm>
          )}
          startWithLeftCollapsed={false}
        />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminInactiveFeedsPage;
